import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, ConfigProvider, Tooltip, message } from 'antd';
import { throttle, displayText, emptyTemplate, formatDateString } from 'utils';
import PaginationComponent from 'components/pagination';
import * as Permissions from 'constants/permissions';
import { useAutoSize, usePermissions } from 'hooks';
import { useSelector } from 'react-redux';
import * as api from 'api';
import { localize } from 'locales';
import addIcon from 'assets/images/addIcon.png';
import ConfirmModal from '../../../components/confirm-modal';
import { isRequestSuccess } from 'api/client';
import './index.css';

const Role = () => {
	const navigate = useNavigate();

	const [pageSize, setPageSize] = useState(20);
	const [pageList, setPageList] = useState([]);
	const [pageIndex, setPageIndex] = useState(1);
	const [total, setTotal] = useState(0);
	const [ref, tableHeight] = useAutoSize();

	const { permissions } = useSelector((state) => state.user);
	const [confirmModalVisible, setConfirmModalVisible] = useState(false);

	const selectedRoleId = useRef(null);

	useEffect(() => {
		document.documentElement.scrollTop = 0;
		getPageData(pageIndex);
	}, [pageIndex, pageSize]);

	// MARK: - api call
	async function getPageData() {
		try {
			const res = await api.getRoles({
				pageSize: pageSize,
				pageIndex: pageIndex,
			});
			setPageList(res?.result);
			setTotal(res?.total);
		} catch (error) {
			console.log(error);
		}
	}

	const addRole = () => {
		navigate('/permission/role/add');
	};

	const editRole = (id) => {
		navigate(`/permission/role/${id}`);
	};
	const onDeleteRole = (id) => {
		selectedRoleId.current = id;
		setConfirmModalVisible(true);
	};

	function onConfirm() {
		return api
			.deleteRole(selectedRoleId.current)
			.then((res) => {
				if (isRequestSuccess(res)) {
					if (pageIndex === 1) {
						document.documentElement.scrollTop = 0;
						getPageData(pageIndex);
					} else {
						setPageIndex(1);
					}
					message.success(localize('deleteSuccess'));
				} else {
					message.error(localize('deleteFail'));
				}
			})
			.finally(() => setConfirmModalVisible(false));
	}

	// MARK: - table data provider
	const _columns = [
		{
			title: localize('rolesNew'),
			dataIndex: 'role',
			key: 'role',
			ellipsis: {
				showTitle: true,
			},
			width: 150,
			render: (roleName) => (
				<Tooltip placement="topLeft" title={roleName}>
					<span className="app-box-cursor-common">{displayText(roleName)}</span>
				</Tooltip>
			),
		},
		{
			title: localize('description'),
			dataIndex: 'description',
			key: 'description',
			ellipsis: {
				showTitle: false,
			},
			width: 150,
			render: (description) => (
				<Tooltip placement="topLeft" title={description}>
					<span className="app-box-cursor-common">{displayText(description)}</span>
				</Tooltip>
			),
		},
		{
			title: localize('updateDate'),
			dataIndex: 'updatedAt',
			key: 'updatedAt',
			width: 150,
			render: (text) => {
				return <div className="app-list-date-style">{formatDateString(text)}</div>;
			},
		},
		{
			title: localize('action'),
			key: 'action',
			width: 100,
			fixed: 'right',
			render: (_, role) => {
				// role.builtin == true means it's the Guest role
				if (role.builtin) {
					return null;
				}
				const editBtn = (
					<div className="action-icon-size-all-con" onClick={() => throttle(editRole, 500)(role.id)}>
						<img className="action-icon-size-all" src={require('assets/images/editIcon.png')} />
					</div>
				);

				const deleteBtn = (
					<div className="action-icon-size-all-con" onClick={() => throttle(onDeleteRole, 1000)(role.id)}>
						<img className="action-icon-size-all" src={require('assets/images/deleteIcon.png')} />
					</div>
				);
				const btns = [];
				if (permissions.includes(Permissions.Roles_EDIT)) {
					btns.push(editBtn);
				}
				if (permissions.includes(Permissions.Roles_DELETE)) {
					btns.push(deleteBtn);
				}
				return <div className="action-btn-con">{btns}</div>;
			},
		},
	];
	const columns = () => {
		const clms = [..._columns];
		if (![Permissions.Roles_EDIT, Permissions.Roles_DELETE].some((permission) => permissions.includes(permission))) {
			clms.pop();
		}
		return clms;
	};

	return (
		<div className="app-page-container">
			<div className="app-navbar">
				<div className="app-navbar-text">Roles</div>
				<div
					ref={usePermissions([Permissions.Roles_ADD], permissions)}
					className="app-btn app-btn-primary"
					onClick={addRole}
				>
					<img src={addIcon} className="app-btn-icon" />
					{localize('addRole')}
				</div>
			</div>
			<div className="app-table">
				<div ref={ref} className="app-table-content">
					<ConfigProvider renderEmpty={emptyTemplate}>
						<Table
							rowKey={(record) => record.id}
							columns={columns()}
							dataSource={pageList}
							pagination={false}
							scroll={{ x: 800, y: tableHeight }}
						/>
					</ConfigProvider>
				</div>
				<div className="table-line-all">
					<PaginationComponent
						pagination={{
							current: pageIndex,
							pageSize: pageSize,
							total: total,
						}}
						onChange={(current, pageSize) => {
							setPageIndex(current);
							setPageSize(pageSize);
						}}
					/>
				</div>
			</div>
			<ConfirmModal
				visible={confirmModalVisible}
				onCancel={() => setConfirmModalVisible(false)}
				onConfirm={onConfirm}
				title={'Are you sure delete this Role?'}
			/>
		</div>
	);
};

export default Role;
